import { CreditCard, Globe, LayoutGrid, LogIn, Mail, Webhook } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { Notice, PageHeader } from "../../ui/components";
import { AddressesPanel } from "./AddressesPanel";
import { EmailDeliveryPanel } from "./EmailDeliveryPanel";
import { IntegrationsOverview } from "./IntegrationsOverview";
import { PaymentsPanel } from "./PaymentsPanel";
import { SSOPanel } from "./SSOPanel";
import { WebhooksPanel } from "./WebhooksPanel";

type Section = "overview" | "payments" | "addresses" | "email" | "webhooks" | "sso";

const sections: Array<{ id: Section; label: string; icon: typeof LayoutGrid }> = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "addresses", label: "Web addresses", icon: Globe },
  { id: "email", label: "Email delivery", icon: Mail },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "sso", label: "Workforce SSO", icon: LogIn },
];

// Every connection this workspace makes to the outside world, in one place. They were previously
// scattered across three settings tabs, which meant nobody could answer "what are we connected to"
// without knowing where each one had been filed.
export function IntegrationsPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const membership = auth.session?.selectedMembership;
  const requested = location.pathname.split("/").filter(Boolean)[1] as Section | undefined;
  const section: Section =
    requested && sections.some((item) => item.id === requested) ? requested : "overview";

  // The gate is here rather than inside each panel so a role without the permission never issues
  // a request it is not allowed to make.
  if (!membershipAllows(membership, "integrations.manage")) {
    return (
      <>
        <PageHeader
          title="Integrations"
          description="Connect this workspace to payments, email, web addresses, and other systems."
        />
        <Notice tone="warning">
          Your role cannot manage integrations. Ask an owner or administrator for access.
        </Notice>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Integrations"
        description="Connect this workspace to payments, email, web addresses, and other systems."
      />
      <nav className="inline settings-tabs" aria-label="Integration sections">
        {sections.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={`button ${section === item.id ? "primary" : "secondary"}`}
              type="button"
              aria-current={section === item.id ? "page" : undefined}
              onClick={() => navigate(`/integrations/${item.id}`)}
            >
              <Icon size={17} /> {item.label}
            </button>
          );
        })}
      </nav>
      <div className="settings-content">
        {section === "overview" ? (
          <IntegrationsOverview onOpen={(target) => navigate(`/integrations/${target}`)} />
        ) : null}
        {section === "payments" ? <PaymentsPanel /> : null}
        {section === "addresses" ? <AddressesPanel /> : null}
        {section === "email" ? <EmailDeliveryPanel /> : null}
        {section === "webhooks" ? <WebhooksPanel /> : null}
        {section === "sso" ? <SSOPanel /> : null}
      </div>
    </>
  );
}
